import { type ForwardedRef, forwardRef, useEffect } from 'react';

import {
  type ListEpmMasterVddsQuery,
  type ListEpmMasterVddsQueryVariables,
  useListEpmMasterVddsQuery,
} from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListEpmMasterVddsQueryVariables, 'pageNumber' | 'pageSize'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListEpmMasterVddsQuery['listEpmMasterVdds']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListEpmMasterVddsQueryVariables, Data> => {
  const { data, ...rest } = useListEpmMasterVddsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      enabled: variables?.enabled ?? null,
      keyword: variables?.keyword ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listEpmMasterVdds?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
      })) || [],
    ...rest,
  };
};

export const EpmMasterVddSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const EpmMasterVddSelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
  return <SelectMultiple multipleSelectedLabel={length => `${length} VDDs`} {...props} data={data} loading={loading} />;
};
